import { Controller } from '@hotwired/stimulus'

export default class MainMenuMobileController extends Controller {
  static targets = ['panel', 'header']

  declare readonly panelTargets: HTMLElement[]
  declare readonly headerTarget: HTMLElement
  declare readonly hasPanelTarget: boolean
  declare readonly hasHeaderTarget: boolean

  private hideTimeout: ReturnType<typeof setTimeout> | null = null

  // ── Lifecycle ────────────────────────────────────────────────────

  connect() {
    this.element.addEventListener('drawer:closed', this.onDrawerClosed)
  }

  disconnect() {
    this.element.removeEventListener('drawer:closed', this.onDrawerClosed)
  }

  // ── Mobile menu methods ──────────────────────────────────────────

  /**
   * Opens a mobile submenu panel.
   * Mirrors Hydrogen Transition:
   *   1. Main slides OUT to the left  (opacity 1→0, translateX 0→-1rem)
   *   2. afterLeave → sub slides IN from the right (opacity 0→1, translateX 1rem→0)
   */
  openSubmenu(event: Event) {
    const button = event.currentTarget as HTMLElement
    const panelId = button.getAttribute('data-panel')
    if (!panelId) return

    const panel = document.getElementById(panelId)
    const main = this.element.querySelector<HTMLElement>(
      '[data-mobile-menu-main]',
    )
    if (!panel) return

    // ── Step 1: animate main OUT to the left ──────────────────────
    if (main && !main.hasAttribute('hidden')) {
      main.classList.add(
        'transition-[opacity,transform]',
        'duration-300',
        'ease-in-out',
      )
      main.classList.add('opacity-0', '-translate-x-4')
      main.classList.remove('opacity-100', 'translate-x-0')

      main.addEventListener(
        'transitionend',
        () => {
          main.setAttribute('hidden', '')
          main.classList.remove(
            'transition-[opacity,transform]',
            'duration-300',
            'ease-in-out',
          )

          // ── Step 2 (afterLeave): animate sub IN from the right ───
          this.showBackButton()
          panel.removeAttribute('hidden')

          // Set enter-from state (opacity 0, shifted right)
          panel.classList.remove('opacity-100', 'translate-x-0')
          panel.classList.add('opacity-0', 'translate-x-4')

          // Force reflow so the browser registers the start state
          panel.offsetHeight

          panel.classList.add(
            'transition-[opacity,transform]',
            'duration-300',
            'ease-in-out',
          )
          panel.classList.add('opacity-100', 'translate-x-0')
          panel.classList.remove('opacity-0', 'translate-x-4')

          panel.addEventListener(
            'transitionend',
            () => {
              panel.classList.remove(
                'transition-[opacity,transform]',
                'duration-300',
                'ease-in-out',
              )
              // afterEnter: focus back button
              const backBtn = this.element.querySelector<HTMLElement>(
                '.mobile-menu-back-button',
              )
              backBtn?.focus()
            },
            { once: true },
          )
        },
        { once: true },
      )
    } else {
      // Main already hidden — just show the sub directly
      this.showBackButton()
      panel.removeAttribute('hidden')
      panel.classList.remove('opacity-100', 'translate-x-0')
      panel.classList.add('opacity-0', 'translate-x-4')
      panel.offsetHeight
      panel.classList.add(
        'transition-[opacity,transform]',
        'duration-300',
        'ease-in-out',
        'opacity-100',
        'translate-x-0',
      )
      panel.classList.remove('opacity-0', 'translate-x-4')
    }
  }

  /**
   * Closes the current mobile submenu panel and returns to main menu.
   * Mirrors Hydrogen Transition:
   *   1. Sub slides OUT to the right (opacity 1→0, translateX 0→1rem)
   *   2. afterLeave → main slides IN from the left (opacity 0→1, translateX -1rem→0)
   */
  closeSubmenu() {
    const visiblePanel = this.panelTargets.find(
      (panel) =>
        !panel.hasAttribute('hidden') &&
        panel.classList.contains('translate-x-0'),
    )
    const main = this.element.querySelector<HTMLElement>(
      '[data-mobile-menu-main]',
    )

    if (!visiblePanel) return

    this.hideBackButton()

    // ── Step 1: animate sub OUT to the right ─────────────────────
    visiblePanel.classList.add(
      'transition-[opacity,transform]',
      'duration-300',
      'ease-in-out',
    )
    visiblePanel.classList.add('opacity-0', 'translate-x-4')
    visiblePanel.classList.remove('opacity-100', 'translate-x-0')

    visiblePanel.addEventListener(
      'transitionend',
      () => {
        visiblePanel.setAttribute('hidden', '')
        visiblePanel.classList.remove(
          'transition-[opacity,transform]',
          'duration-300',
          'ease-in-out',
          'opacity-0',
          'translate-x-4',
        )

        // ── Step 2 (afterLeave): animate main IN from the left ────
        if (main) {
          main.removeAttribute('hidden')

          // Set enter-from state (opacity 0, shifted left)
          main.classList.remove('opacity-100', 'translate-x-0')
          main.classList.add('opacity-0', '-translate-x-4')

          // Force reflow
          main.offsetHeight

          main.classList.add(
            'transition-[opacity,transform]',
            'duration-300',
            'ease-in-out',
          )
          main.classList.add('opacity-100', 'translate-x-0')
          main.classList.remove('opacity-0', '-translate-x-4')

          main.addEventListener(
            'transitionend',
            () => {
              main.classList.remove(
                'transition-[opacity,transform]',
                'duration-300',
                'ease-in-out',
              )
            },
            { once: true },
          )
        }
      },
      { once: true },
    )
  }

  /**
   * Handles drawer close events to reset mobile menu to main menu.
   */
  private onDrawerClosed = () => {
    this.resetMobileMenu()
  }

  /**
   * Resets mobile menu to initial state — no animation, instant reset.
   */
  private resetMobileMenu() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }

    // Hard-reset all sub panels
    this.panelTargets.forEach((panel) => {
      panel.setAttribute('hidden', '')
      panel.classList.remove(
        'opacity-100',
        'translate-x-0',
        'opacity-0',
        'translate-x-4',
        'transition-[opacity,transform]',
        'duration-300',
        'ease-in-out',
      )
    })

    // Hard-reset main panel back to visible
    const main = this.element.querySelector<HTMLElement>(
      '[data-mobile-menu-main]',
    )
    if (main) {
      main.removeAttribute('hidden')
      main.classList.remove(
        'opacity-0',
        '-translate-x-4',
        'transition-[opacity,transform]',
        'duration-300',
        'ease-in-out',
      )
      main.classList.add('opacity-100', 'translate-x-0')
    }

    this.hideBackButton()
  }

  /**
   * Shows the back button in header by removing the hidden class.
   */
  private showBackButton() {
    const backButton = this.element.querySelector('.mobile-menu-back-button')
    if (backButton) {
      backButton.classList.remove('hidden')
    }
  }

  /**
   * Hides the back button in header by adding the hidden class.
   */
  private hideBackButton() {
    const backButton = this.element.querySelector('.mobile-menu-back-button')
    if (backButton) {
      backButton.classList.add('hidden')
    }
  }
}
